import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { COLOR_SELECT_ROW_GANT, propsArrowLine } from '../const';
import {
    getBondNameByBondType,
    getPointXByGeometryParamDragItem,
    getPointYByGeometryParamDragItem,
    getTypeBondByDragging,
} from '../functions';
import useGetGeometryParamForItem from '../hooks/useGetGeometryParamForItem';
import useKeyBoard from '../hooks/useKeyBoard';
import useStatusEditProject from '../hooks/useStatusEditProject';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { BondType, DraggableGantType, ItemType, MappingItemIdIndexType } from '../types';
import GantGridArrowEE from './gantGridArrowEE';
import GantGridArrowES from './gantGridArrowES';
import GantGridArrowPick from './gantGridArrowPick';
import GantGridArrowSE from './gantGridArrowSE';
import GantGridArrowSS from './gantGridArrowSS';

interface IGantGridArrow {
    startItem: ItemType;
    endItem: ItemType | null;
    draggingStart: DraggableGantType;
    draggingEnd: DraggableGantType;
    isAbsolute?: boolean;
}

const GantGridArrow: React.FC<IGantGridArrow> = ({ startItem, endItem, draggingStart, draggingEnd, isAbsolute }) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);

    const isEditableProject = useStatusEditProject(startItem.projectId);

    const isEscapePressed = useKeyBoard('Escape');
    const isDeletePressed = useKeyBoard('Delete');
    const isBackspacePressed = useKeyBoard('Backspace');
    const [activeArrow, setActiveArrow] = useState(false);
    const [selectArrow, setSelectArrow] = useState(false);

    const geometryParamStartItem = useGetGeometryParamForItem(startItem);
    const geometryParamEndItem = useGetGeometryParamForItem(endItem);

    const pointStartX = getPointXByGeometryParamDragItem(draggingStart, geometryParamStartItem);
    const pointStartY = getPointYByGeometryParamDragItem(geometryParamStartItem);

    const pointEndX = getPointXByGeometryParamDragItem(draggingEnd, geometryParamEndItem);
    const pointEndY = getPointYByGeometryParamDragItem(geometryParamEndItem);

    const extendPropsArrowLine = {
        ...propsArrowLine,
        strokeWidth: activeArrow ? '2' : '1',
        stroke: selectArrow ? COLOR_SELECT_ROW_GANT : '#878787',
    };

    useEffect(() => {
        if (isEscapePressed) {
            setSelectArrow(false);
        }
    }, [isEscapePressed]);

    useEffect(() => {
        if ((isDeletePressed || isBackspacePressed) && selectArrow && startItem && endItem) {
            const newItems = [...itemsList];

            const deleteBonds = (typeBond: BondType, isParentItem?: boolean) => {
                const bondName = getBondNameByBondType(typeBond, true);
                const mainItemId = (isParentItem ? startItem : endItem)?.id;
                const childItemId = (isParentItem ? endItem : startItem)?.id;

                const oldItem = newItems[mappingItemIdIndex[mainItemId]];
                const newBonds = (oldItem[bondName] as string[]).filter((bondId) => childItemId !== bondId);
                newItems[mappingItemIdIndex[mainItemId]] = {
                    ...oldItem,
                    [bondName]: newBonds,
                };
            };

            const typeBond = getTypeBondByDragging(draggingStart, draggingEnd);

            deleteBonds(typeBond, true);
            deleteBonds(typeBond);

            dispatch(setterGantItems({ itemsList: newItems }));
        }
    }, [isDeletePressed, isBackspacePressed]);

    const typeBond = getTypeBondByDragging(draggingStart, draggingEnd);

    return (
        <>
            {startItem && endItem && (
                <g
                    transform={`translate(0, ${isAbsolute ? -geometryParamStartItem.startY : 0})`}
                    onMouseOver={() => {
                        setActiveArrow(true);
                    }}
                    onMouseLeave={() => {
                        setActiveArrow(false);
                    }}
                    onClick={() => {
                        if (isEditableProject) {
                            setSelectArrow(!selectArrow);
                        }
                    }}
                >
                    {typeBond === 'SS' && (
                        <GantGridArrowSS
                            pointStart={{ x: pointStartX, y: pointStartY }}
                            pointEnd={{ x: pointEndX, y: pointEndY }}
                            propsArrowLine={extendPropsArrowLine}
                        />
                    )}
                    {typeBond === 'EE' && (
                        <GantGridArrowEE
                            pointStart={{ x: pointStartX, y: pointStartY }}
                            pointEnd={{ x: pointEndX, y: pointEndY }}
                            propsArrowLine={extendPropsArrowLine}
                        />
                    )}
                    {typeBond === 'SE' && (
                        <GantGridArrowSE
                            pointStart={{ x: pointStartX, y: pointStartY }}
                            pointEnd={{ x: pointEndX, y: pointEndY }}
                            propsArrowLine={extendPropsArrowLine}
                        />
                    )}
                    {typeBond === 'ES' && (
                        <GantGridArrowES
                            pointStart={{ x: pointStartX, y: pointStartY }}
                            pointEnd={{ x: pointEndX, y: pointEndY }}
                            propsArrowLine={extendPropsArrowLine}
                        />
                    )}
                    <GantGridArrowPick
                        endItem={endItem}
                        draggingEnd={draggingEnd}
                        propsArrowLine={extendPropsArrowLine}
                        selectArrow={selectArrow}
                    />
                </g>
            )}
        </>
    );
};

export default React.memo(GantGridArrow);
